import { Controller } from "@hotwired/stimulus"

// Handles testing API connections from the integrations page
// Reads API key from input field and makes test request to validate
export default class extends Controller {
  static targets = ["input", "button", "status"]
  static values = {
    url: String,
    service: String,
    hasExisting: { type: Boolean, default: false }
  }

  async test(event) {
    event.preventDefault()

    const apiKey = this.hasInputTarget ? this.inputTarget.value : null
    // If no new key entered but we have an existing saved key, test that instead
    const useExisting = (!apiKey || apiKey.trim() === "") && this.hasExistingValue

    if (!apiKey && !useExisting) {
      this.showStatus("error", "Please enter an API key first")
      return
    }

    this.setLoading(true)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          service: this.serviceValue,
          api_key: apiKey || null,
          use_existing: useExisting
        })
      })

      const result = await response.json()

      if (result.success) {
        this.showStatus("success", result.message)
      } else {
        this.showStatus("error", result.message)
      }
    } catch (error) {
      this.showStatus("error", `Connection failed: ${error.message}`)
    } finally {
      this.setLoading(false)
    }
  }

  // Test using existing saved credentials (no input field required)
  async testExisting(event) {
    event.preventDefault()

    if (!this.hasExistingValue) {
      this.showStatus("error", "Please save credentials first")
      return
    }

    this.setLoading(true)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          service: this.serviceValue,
          use_existing: true
        })
      })

      const result = await response.json()

      if (result.success) {
        this.showStatus("success", result.message)
      } else {
        this.showStatus("error", result.message)
      }
    } catch (error) {
      this.showStatus("error", `Connection failed: ${error.message}`)
    } finally {
      this.setLoading(false)
    }
  }

  setLoading(loading) {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = loading
      this.buttonTarget.textContent = loading ? "Testing..." : "Test Connection"
    }
  }

  showStatus(type, message) {
    if (!this.hasStatusTarget) return

    this.statusTarget.classList.remove("hidden", "text-green-600", "text-red-600", "dark:text-green-400", "dark:text-red-400")

    if (type === "success") {
      this.statusTarget.classList.add("text-green-600", "dark:text-green-400")
    } else {
      this.statusTarget.classList.add("text-red-600", "dark:text-red-400")
    }

    this.statusTarget.textContent = message

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.statusTarget.classList.add("hidden")
    }, 5000)
  }
}
